import React, { useEffect, useMemo, useState } from 'react';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import {
  peekAuthorizedImage,
  preloadAuthorizedImage,
} from '../utils/authorizedImageCache';

const isBlobOrDataUrl = (u) =>
  typeof u === 'string' && (/^blob:/i.test(u) || /^data:/i.test(u));

/**
 * Renders image/video from API URLs using the same `token` header as RTK (plain &lt;img src&gt; cannot).
 */
export function AuthorizedMedia({
  src,
  alt = '',
  className = '',
  as = 'image',
  muted,
  playsInline,
  onError,
  /** When `as="video"`, show native controls (e.g. complaint detail view). */
  videoControls = false,
}) {
  const resolved = useMemo(() => resolveAssetUrl(src), [src]);
  const [displaySrc, setDisplaySrc] = useState(() => peekAuthorizedImage(src) || '');

  useEffect(() => {
    if (!resolved) {
      setDisplaySrc('');
      return undefined;
    }

    if (isBlobOrDataUrl(resolved)) {
      setDisplaySrc(resolved);
      return undefined;
    }

    const cached = peekAuthorizedImage(resolved);
    if (cached) {
      setDisplaySrc(cached);
      return undefined;
    }

    let cancelled = false;
    preloadAuthorizedImage(resolved).then((url) => {
      if (!cancelled) setDisplaySrc(url || '');
    });

    return () => {
      cancelled = true;
    };
  }, [resolved, src]);

  if (!displaySrc) return null;

  if (as === 'video') {
    return (
      <video
        src={displaySrc}
        className={className}
        muted={muted}
        playsInline={playsInline}
        controls={Boolean(videoControls)}
        preload="metadata"
        onError={onError}
      />
    );
  }

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={className}
      decoding="async"
      onError={onError}
    />
  );
}
